import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  Input,
  OnDestroy,
  Output,
  untracked,
} from '@angular/core';
import {
  PickListModule,
  PickListMoveToSourceEvent,
  PickListMoveToTargetEvent,
} from 'primeng/picklist';
import { TopicStore } from '../../../stores/topic/topic.state';
import { Topic } from '../../../models/topic';
import { DividerModule } from 'primeng/divider';
import { PrimeTemplate } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { AlertComponent } from '../../atoms/alert/alert.component';
import {
  addById,
  haveSameValues,
  removeByIds,
} from '../../../utility/array.utility';

/**
 * Pick list for topics backed by the `TopicStore` selection.
 *
 * The store selection is the single source of truth, both lists are derived from it.
 * `selectedIds` is taken over as soon as the store has loaded its items.
 */
import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
@Component({
  selector: 'app-topic-pick-list',
  standalone: true,
  imports: [
    DividerModule,
    PickListModule,
    PrimeTemplate,
    TagModule,
    AlertComponent,
    PickListHeaderComponent,
  ],
  templateUrl: './topic-pick-list.component.html',
  styleUrl: './topic-pick-list.component.scss',
})
export class TopicPickListComponent implements OnDestroy {
  /** Preselected ids; without it the current store selection is used. */
  readonly selectedIds = input<string[] | undefined>([]);
  @Input() keepSelection = false;
  @Input() showSourceControls = false;
  /** Reordering the target is not persisted, so the controls are hidden by default. */
  @Input() showTargetControls = false;
  @Output() selectionChanged = new EventEmitter<string[]>();

  readonly topicStore = inject(TopicStore);

  private readonly selectedIdSet = computed(
    () => new Set(this.topicStore.selection().map(item => item._id))
  );

  /** Items which are not selected yet. */
  readonly sourceList = computed(() => {
    const selected = this.selectedIdSet();
    return this.topicStore
      .asArray()
      .filter((topic: Topic) => !selected.has(topic._id));
  });

  readonly targetList = computed(() => [...this.topicStore.selection()]);

  constructor() {
    effect(
      () => {
        const ids = this.selectedIds();
        this.topicStore.items();
        if (!ids) return;
        untracked(() => {
          if (!haveSameValues(ids, [...this.selectedIdSet()])) {
            this.topicStore.selectMultiple(ids);
          }
        });
      },
      { allowSignalWrites: true }
    );
  }

  /**
   * Adds the moved items to the store selection and emits the new selection.
   *
   * @param {PickListMoveToTargetEvent} event - Contains the moved items.
   */
  addToSelection(event: PickListMoveToTargetEvent): void {
    this.topicStore.updateSelection(
      addById(this.topicStore.selection(), event.items as Topic[])
    );
    this.emitSelection();
  }

  /**
   * Removes the moved items from the store selection and emits the new selection.
   *
   * @param {PickListMoveToSourceEvent} event - Contains the moved items.
   */
  removeFromSelection(event: PickListMoveToSourceEvent): void {
    this.topicStore.updateSelection(
      removeByIds(
        this.topicStore.selection(),
        (event.items as Topic[]).map(item => item._id)
      )
    );
    this.emitSelection();
  }

  private emitSelection(): void {
    this.selectionChanged.emit(
      this.topicStore.selection().map(item => item._id)
    );
  }

  /**
   * Clears the selection on destroy unless `keepSelection` is set.
   */
  ngOnDestroy(): void {
    if (this.keepSelection) return;
    this.topicStore.clearSelection();
  }
}
